import { useEffect, useState } from "react";
import { Button, Form, Input, InputNumber, Modal, Popconfirm, Switch, Table, Tag, Upload, message } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import PageLayout from "@layouts/PageLayout";
import { mediaUpload } from "@services/upload.service";
import {
  createProduct,
  deleteProduct,
  formatInr,
  getInventory,
  updateProduct,
} from "@services/shop.service";
import AdminFrame from "./AdminFrame";
import { PAGE_SIZE } from "./list";
import "./shop.css";

const empty = {
  name: "",
  description: "",
  price: 999,
  compare_at_price: 0,
  category: "General",
  stock: 10,
  sku: "",
  image_url: "",
  is_active: true,
};

function AdminProductsPage() {
  const [products, setProducts] = useState<any[]>([]);
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [form] = Form.useForm();

  const load = async (nextPage = page, term = query) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: String(nextPage),
        page_size: String(PAGE_SIZE),
      });
      if (term.trim()) params.set("search", term.trim());
      const resp = await getInventory(`?${params}`);
      setProducts(resp?.products || []);
      setTotal(Number(resp?.total || 0));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(page, query);
  }, [page, query]);

  const save = async (values: any) => {
    if (editing?.id) {
      await updateProduct(editing.id, values);
      message.success("Product updated");
    } else {
      await createProduct(values);
      message.success("Product added");
    }
    setOpen(false);
    setEditing(null);
    form.resetFields();
    load(page, query);
  };

  return (
    <PageLayout>
      <AdminFrame
        title="Products"
        meta={`${total} in the catalog`}
        page={page}
        total={total}
        onPage={setPage}
        extra={
          <>
            <Input.Search
              allowClear
              placeholder="Search name, SKU, or category"
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(1);
              }}
            />
            <Button
              type="primary"
              onClick={() => {
                setEditing(null);
                form.setFieldsValue(empty);
                setOpen(true);
              }}
            >
              Add product
            </Button>
          </>
        }
      >
        <Table
          className="data-table"
          rowKey="id"
          loading={loading}
          dataSource={products}
          locale={{ emptyText: query.trim() ? "No products match that search" : "No products yet" }}
          pagination={false}
          scroll={{ x: 860 }}
          columns={[
            { title: "Name", dataIndex: "name" },
            { title: "Category", dataIndex: "category" },
            { title: "SKU", dataIndex: "sku" },
            { title: "Price", dataIndex: "price", render: (value) => formatInr(value) },
            { title: "Stock", dataIndex: "stock" },
            {
              title: "Active",
              dataIndex: "is_active",
              render: (value) => <Tag color={value ? "green" : "default"}>{value ? "Visible" : "Hidden"}</Tag>,
            },
            {
              title: "",
              render: (_, record: any) => (
                <>
                  <Button
                    type="link"
                    onClick={() => {
                      setEditing(record);
                      form.setFieldsValue(record);
                      setOpen(true);
                    }}
                  >
                    Edit
                  </Button>
                  <Popconfirm
                    title="Delete this product?"
                    onConfirm={async () => {
                      await deleteProduct(record.id);
                      message.success("Deleted");
                      load();
                    }}
                  >
                    <Button type="link" danger>
                      Delete
                    </Button>
                  </Popconfirm>
                </>
              ),
            },
          ]}
        />
      </AdminFrame>
        <Modal
          className="product-modal"
          width={840}
          title={editing ? "Edit product" : "Add product"}
          open={open}
          onCancel={() => setOpen(false)}
          onOk={() => form.submit()}
          okText={editing ? "Save changes" : "Create product"}
          cancelText="Cancel"
          centered
        >
          <Form form={form} layout="vertical" onFinish={save} initialValues={empty} className="product-grid-form">
            <Form.Item className="span-3" name="name" label="Name" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="sku" label="SKU" rules={[{ required: true }]}>
              <Input />
            </Form.Item>
            <Form.Item name="category" label="Category">
              <Input />
            </Form.Item>
            <Form.Item name="stock" label="Stock">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item name="price" label="Price" rules={[{ required: true }]}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item className="span-2" name="compare_at_price" label="Compare-at price">
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item className="span-3" label="Product image">
              <div className="image-row">
                <Form.Item name="image_url" noStyle>
                  <Input placeholder="Image URL" />
                </Form.Item>
                <Upload
                  maxCount={1}
                  showUploadList={false}
                  accept="image/png,image/jpeg"
                  customRequest={async ({ file, onSuccess, onError }) => {
                    const body = new FormData();
                    body.append("file", file as Blob);
                    try {
                      const resp = await mediaUpload(body);
                      const url = resp?.data?.url || "";
                      form.setFieldValue("image_url", url);
                      message.success("Image uploaded");
                      onSuccess?.(resp);
                    } catch (error) {
                      onError?.(error as Error);
                    }
                  }}
                >
                  <Button icon={<UploadOutlined />}>Upload</Button>
                </Upload>
              </div>
            </Form.Item>
            <Form.Item className="span-3" name="description" label="Description">
              <Input.TextArea rows={3} />
            </Form.Item>
            <Form.Item className="span-3" name="is_active" label="Visible in store" valuePropName="checked">
              <Switch />
            </Form.Item>
          </Form>
        </Modal>
    </PageLayout>
  );
}

export default AdminProductsPage;
